import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

// Cross-tab sync constants
const SETTINGS_SYNC_KEY = "__site_settings_sync__";
const SETTINGS_CACHE_KEY = "__site_settings_cache__";

export interface NavLink {
  href: string;
  label: string;
}

export interface SocialLink {
  icon: string;
  href: string;
  label: string;
}

export interface FooterSection {
  title: string;
  links: { label: string; href: string }[];
}

export interface HeaderSettings {
  logo: string;
  navLinks: NavLink[];
  contactButtonText: string;
  contactButtonLink: string;
}

export interface FooterSettings {
  brandDescription: string;
  socialLinks: SocialLink[];
  footerSections: FooterSection[];
  copyrightText: string;
  builtWithText: string;
}

export interface Stat {
  number: string;
  label: string;
}

export interface HomeSettings {
  badge: string;
  name: string;
  titleLine1: string;
  titleLine2: string;
  description: string;
  ctaPrimary: { text: string; link: string };
  ctaSecondary: { text: string; link: string };
  featuredWorkTitle: string;
  featuredWorkDescription: string;
  skillsPreviewTitle: string;
  skillsPreviewDescription1: string;
  skillsPreviewDescription2: string;
  skillsPreviewButtonText: string;
  skillsPreviewButtonLink: string;
  stats: Stat[];
  ctaSectionTitle: string;
  ctaSectionDescription: string;
  ctaSectionPrimaryText: string;
  ctaSectionPrimaryLink: string;
  ctaSectionSecondaryText: string;
  ctaSectionSecondaryLink: string;
}

export interface PortfolioSettings {
  title: string;
  description: string;
}

export interface BlogSettings {
  title: string;
  description: string;
}

// WebtoolsTool is deprecated - tools are now stored in the database
// Kept for backward compatibility with existing site_settings
export interface WebtoolsTool {
  id?: string;
  title: string;
  description: string;
  url: string | null;
  category: string;
  icon: string;
  hasInternalPage?: boolean;
  fullDescription?: string;
  instructions?: string;
  embedCode?: string;
  demoUrl?: string;
  demoType?: 'html' | 'scorm' | 'storyline' | 'iframe' | 'webapp';
  mediaImages?: string[];
  mediaVideo?: string;
}

// Page-level settings only - tools are now in the webtools database table
export interface WebtoolsSettings {
  title: string;
  description: string;
  intro: string;
  whyTitle: string;
  whyDescription: string;
  whyCta: string;
  // Deprecated: tools are now stored in database, kept for backward compat
  tools?: WebtoolsTool[];
  /** Set when the tools list was left out of the download (public pages). */
  toolsOmitted?: boolean;
}

export interface AboutSocialLink {
  icon: string;
  href: string;
  label: string;
}

export interface Skill {
  /** Stable identifier used for drag-and-drop + React keys in the admin editor */
  id?: string;
  name: string;
  category: string;
  icon: string;
  proficiency: number;
}

export interface PracticeArea {
  /** Stable identifier used for drag-and-drop + React keys in the admin editor */
  id?: string;
  title: string;
  description: string;
  icon?: string;
}

// Legacy alias for backward compatibility
export type Experience = PracticeArea;

export interface AboutSettings {
  title: string;
  intro: string;
  paragraph1: string;
  paragraph2: string;
  profileImage: string;
  location: string;
  showLocation?: boolean;
  email: string;
  socialLinks: AboutSocialLink[];
  contactTitle: string;
  contactDescription: string;
  skillsTitle: string;
  skillsDescription: string;
  skillCategories: string[];
  skills: Skill[];
  practiceAreasTitle: string;
  practiceAreasDescription: string;
  practiceAreas: PracticeArea[];
}

export interface ThemeColorSet {
  primary: string;
  accent: string;
  background: string;
  foreground: string;
  card: string;
  muted: string;
}

export interface ThemeFonts {
  heading: string;
  body: string;
}

export interface ThemeLookAndFeel {
  borderRadius: "none" | "subtle" | "rounded" | "pill";
  cardRadius: "none" | "subtle" | "rounded" | "extra-rounded";
  shadowIntensity: "none" | "subtle" | "medium" | "dramatic";
  animationSpeed: "none" | "snappy" | "smooth" | "slow";
  buttonStyle: "flat" | "outlined" | "gradient" | "soft";
  cardBorderStyle: "none" | "subtle" | "solid" | "accent";
  density: "compact" | "comfortable" | "spacious";
  buttonTypography: "normal" | "bold" | "uppercase" | "bold-uppercase";
  buttonHoverEffect: "none" | "lift" | "scale" | "glow" | "lift-scale" | "pulse" | "border-glow" | "shake" | "bounce";
  navLinkHoverEffect?: "none" | "underline" | "scale" | "fade" | "highlight" | "slide-bg" | "dot" | "border-bottom" | "text-glow" | "lift";
  showThemeToggle?: boolean;
  defaultTheme?: "light" | "dark" | "system";
}

export interface SavedTheme {
  id: string;
  name: string;
  light: ThemeColorSet;
  dark: ThemeColorSet;
  fonts: ThemeFonts;
  lookAndFeel: ThemeLookAndFeel;
  createdAt: string;
}

export interface ThemeSettings {
  light: ThemeColorSet;
  dark: ThemeColorSet;
  fonts: ThemeFonts;
  favicon?: string;
  lookAndFeel: ThemeLookAndFeel;
  savedThemes: SavedTheme[];
}

export interface AnalyticsSettings {
  googleAnalyticsId: string;
  showDebugDashboard: boolean;
}

export interface SearchSettings {
  enabled: boolean;
  minQueryLength: number;
  maxResults: number;
  showRecentSearches: boolean;
}

const defaultHeaderSettings: HeaderSettings = {
  logo: "Nathan Stoker",
  navLinks: [
    { href: "/", label: "Home" },
    { href: "/portfolio", label: "Portfolio" },
    { href: "/webtools", label: "Webtools" },
    { href: "/store", label: "Store" },
    { href: "/blog", label: "Blog" },
    { href: "/about", label: "About" },
  ],
  contactButtonText: "Contact",
  contactButtonLink: "/about#contact",
};

const defaultFooterSettings: FooterSettings = {
  brandDescription:
    "Security Multimedia & Instructional Design Lead specializing in creating engaging eLearning experiences, custom SharePoint solutions, and compelling visual content.",
  socialLinks: [
    { icon: "Github", href: "https://github.com", label: "GitHub" },
    { icon: "Linkedin", href: "https://linkedin.com", label: "LinkedIn" },
    { icon: "Mail", href: "mailto:hello@natestoker.com", label: "Email" },
  ],
  footerSections: [
    {
      title: "Navigation",
      links: [
        { label: "Home", href: "/" },
        { label: "Portfolio", href: "/portfolio" },
        { label: "Webtools", href: "/webtools" },
        { label: "Blog", href: "/blog" },
        { label: "About", href: "/about" },
      ],
    },
    {
      title: "Categories",
      links: [
        { label: "eLearning", href: "/portfolio?category=eLearning" },
        { label: "SharePoint", href: "/portfolio?category=SharePoint Apps" },
        { label: "3D & Animation", href: "/portfolio?category=3D" },
        { label: "Graphic Design", href: "/portfolio?category=Graphic Design" },
      ],
    },
  ],
  copyrightText: "Nathan Stoker. All rights reserved.",
  builtWithText: "Built with React, TypeScript & Tailwind CSS",
};

const defaultHomeSettings: HomeSettings = {
  badge: "Available for projects",
  name: "Nathan Stoker",
  titleLine1: "Security Multimedia &",
  titleLine2: "Instructional Design Lead",
  description: "Creating engaging eLearning experiences, custom SharePoint solutions, and compelling visual content that transforms complex concepts into memorable learning journeys.",
  ctaPrimary: { text: "View My Work", link: "/portfolio" },
  ctaSecondary: { text: "Get in Touch", link: "/about#contact" },
  featuredWorkTitle: "Featured Work",
  featuredWorkDescription: "A selection of projects showcasing my expertise in eLearning development, multimedia design, and custom application building.",
  skillsPreviewTitle: "Building Learning Experiences That Stick",
  skillsPreviewDescription1: "With over a decade of experience in instructional design and multimedia development, I specialize in creating engaging content that transforms how organizations train their teams.",
  skillsPreviewDescription2: "From interactive eLearning modules to custom SharePoint applications, I bring technical expertise and creative vision to every project.",
  skillsPreviewButtonText: "Learn More About Me",
  skillsPreviewButtonLink: "/about",
  stats: [
    { number: "100+", label: "Projects Completed" },
    { number: "50+", label: "Clients Served" },
    { number: "10+", label: "Years Experience" },
    { number: "100k+", label: "Learners Reached" },
  ],
  ctaSectionTitle: "Ready to Transform Your Training?",
  ctaSectionDescription: "Let's discuss how we can create engaging learning experiences for your organization.",
  ctaSectionPrimaryText: "Start a Conversation",
  ctaSectionPrimaryLink: "/about#contact",
  ctaSectionSecondaryText: "Read the Blog",
  ctaSectionSecondaryLink: "/blog",
};

const defaultPortfolioSettings: PortfolioSettings = {
  title: "Portfolio",
  description: "A comprehensive collection of my work spanning eLearning development, SharePoint solutions, 3D visualization, animation, and graphic design.",
};

const defaultBlogSettings: BlogSettings = {
  title: "Blog",
  description: "Thoughts, tutorials, and insights on eLearning development, instructional design, and multimedia creation.",
};

const defaultWebtoolsSettings: WebtoolsSettings = {
  title: "Webtools for eLearning",
  description: "A curated collection of web-based tools and resources to enhance your eLearning development workflow.",
  intro: "Enhance your eLearning projects with these innovative tools by Nathan Stoker. Four are JavaScript-driven solutions tailored for Articulate Storyline, while one offers a unique interactive experience. Each tool boosts functionality, aesthetics, or engagement for seamless course development.",
  whyTitle: "Why Choose These Tools?",
  whyDescription: "From EyeSpy's unique interactivity to Storyline-specific enhancements, these tools make eLearning development intuitive and impactful. Build professional, learner-focused courses with ease.",
  whyCta: "Discover more at natestoker.com and level up your eLearning today!",
  // Tools now stored in database - this array is deprecated
  tools: [],
};

const defaultAboutSettings: AboutSettings = {
  title: "About Me",
  intro: "I'm Nathan Stoker, a Security Multimedia & Instructional Design Lead with over a decade of experience creating engaging learning experiences and custom digital solutions.",
  paragraph1: "My passion lies at the intersection of technology, design, and education. I believe that well-designed learning experiences can transform organizations and empower individuals to reach their full potential.",
  paragraph2: "When I'm not creating eLearning modules or building SharePoint applications, you'll find me exploring new technologies, experimenting with 3D visualization, or sharing insights through my blog.",
  profileImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
  location: "Based in Utah, USA",
  showLocation: true,
  email: "hello@natestoker.com",
  socialLinks: [
    { icon: "Linkedin", href: "https://linkedin.com", label: "LinkedIn Profile" },
    { icon: "Github", href: "https://github.com", label: "GitHub Profile" },
  ],
  contactTitle: "Get in Touch",
  contactDescription: "Have a project in mind? I'd love to hear from you. Send me a message and let's create something amazing together.",
  skillsTitle: "Skills & Technologies",
  skillsDescription: "A comprehensive toolkit for creating exceptional learning experiences and digital solutions.",
  skillCategories: ["eLearning", "Development", "Design", "3D", "Animation", "Standards", "Methodology"],
  skills: [
    { name: "Articulate Storyline", category: "eLearning", icon: "GraduationCap", proficiency: 95 },
    { name: "Articulate Rise", category: "eLearning", icon: "BookOpen", proficiency: 90 },
    { name: "Adobe Captivate", category: "eLearning", icon: "Play", proficiency: 85 },
    { name: "Camtasia", category: "eLearning", icon: "Video", proficiency: 88 },
    { name: "JavaScript", category: "Development", icon: "Code", proficiency: 90 },
    { name: "TypeScript", category: "Development", icon: "FileCode", proficiency: 88 },
    { name: "React", category: "Development", icon: "Layers", proficiency: 85 },
    { name: "SharePoint SPFx", category: "Development", icon: "Layout", proficiency: 88 },
    { name: "HTML5 / CSS3", category: "Development", icon: "Globe", proficiency: 95 },
    { name: "Figma", category: "Design", icon: "Figma", proficiency: 90 },
    { name: "Adobe Photoshop", category: "Design", icon: "Image", proficiency: 92 },
    { name: "Adobe Illustrator", category: "Design", icon: "Pen", proficiency: 88 },
    { name: "After Effects", category: "Design", icon: "Film", proficiency: 85 },
    { name: "Blender", category: "3D", icon: "Box", proficiency: 80 },
    { name: "Three.js", category: "3D", icon: "Cube", proficiency: 75 },
    { name: "Lottie", category: "Animation", icon: "Sparkles", proficiency: 85 },
    { name: "SCORM / xAPI", category: "Standards", icon: "Database", proficiency: 92 },
    { name: "WCAG Accessibility", category: "Standards", icon: "Accessibility", proficiency: 90 },
    { name: "ADDIE / SAM", category: "Methodology", icon: "GitBranch", proficiency: 95 },
  ],
  practiceAreasTitle: "Practice Areas",
  practiceAreasDescription: "Specialized expertise across learning technology, accessibility, and multimedia design.",
  practiceAreas: [
    {
      title: "Secure Learning Systems & Internal Platforms",
      description: "Design and development of enterprise-grade security training, intranet tools, and interactive learning systems aligned with real-world risk and compliance requirements.",
      icon: "Shield"
    },
    {
      title: "Advanced Interactivity & Animation Systems",
      description: "Custom JavaScript-driven interactions, animation frameworks, simulations, and drag-and-drop learning mechanics.",
      icon: "Sparkles"
    },
    {
      title: "Accessibility-First Design",
      description: "WCAG 2.1 AA-compliant design and development across web and learning platforms, with an emphasis on inclusive interaction patterns.",
      icon: "Accessibility"
    },
    {
      title: "Visual Storytelling & Motion Design",
      description: "Illustration, motion graphics, video, and animation for digital learning and product experiences.",
      icon: "Film"
    },
    {
      title: "Global Learning Delivery",
      description: "Localization and adaptation of learning experiences for international audiences across multiple languages and cultural contexts.",
      icon: "Globe"
    }
  ],
};

const defaultThemeSettings: ThemeSettings = {
  light: {
    primary: "208 100% 37%",
    accent: "208 100% 50%",
    background: "0 0% 100%",
    foreground: "0 0% 10%",
    card: "0 0% 98%",
    muted: "0 0% 96%",
  },
  dark: {
    primary: "208 100% 62%",
    accent: "208 100% 62%",
    background: "0 0% 7%",
    foreground: "0 0% 98%",
    card: "0 0% 12%",
    muted: "0 0% 18%",
  },
  fonts: {
    heading: "Inter",
    body: "Inter",
  },
  favicon: "/favicon.ico",
  lookAndFeel: {
    borderRadius: "rounded",
    cardRadius: "rounded",
    shadowIntensity: "medium",
    animationSpeed: "smooth",
    buttonStyle: "flat",
    cardBorderStyle: "subtle",
    density: "comfortable",
    buttonTypography: "bold-uppercase",
    buttonHoverEffect: "lift",
    navLinkHoverEffect: "underline",
    showThemeToggle: true,
    defaultTheme: "system",
  },
  savedThemes: [],
};

const defaultAnalyticsSettings: AnalyticsSettings = {
  googleAnalyticsId: "G-2MR2187D5N",
  showDebugDashboard: false,
};

const defaultSearchSettings: SearchSettings = {
  enabled: true,
  minQueryLength: 2,
  maxResults: 20,
  showRecentSearches: true,
};

// Helper to get cached settings from localStorage
const getCachedSettings = (): { key: string; value: Json }[] | null => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached) as { key: string; value: Json }[];
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

// Helper to save settings to localStorage cache
const setCachedSettings = (data: { key: string; value: Json }[]) => {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

// The "webtools" row still carries the deprecated tools list (about 485 KB,
// including an inline image). Public pages only need its short page fields, so
// outside the admin dashboard fetch those instead of the whole row.
const WEBTOOLS_PAGE_FIELDS = ["title", "description", "intro", "whyTitle", "whyDescription", "whyCta"] as const;
const isAdminPath = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith("/dashboard");

const fetchSiteSettings = async () => {
  if (isAdminPath()) {
    const { data, error } = await supabase.from("site_settings").select("key, value");
    if (error) throw error;
    if (data) setCachedSettings(data);
    return data || [];
  }

  const [rest, webtools] = await Promise.all([
    supabase.from("site_settings").select("key, value").neq("key", "webtools"),
    supabase
      .from("site_settings")
      .select(WEBTOOLS_PAGE_FIELDS.map((f) => `${f}:value->>${f}`).join(", "))
      .eq("key", "webtools")
      .maybeSingle(),
  ]);
  if (rest.error) throw rest.error;
  if (webtools.error) throw webtools.error;

  const data: { key: string; value: Json }[] = [...(rest.data || [])];
  if (webtools.data) {
    const fields = webtools.data as unknown as Record<string, string | null>;
    const value: Record<string, Json> = { toolsOmitted: true };
    for (const f of WEBTOOLS_PAGE_FIELDS) if (fields[f] != null) value[f] = fields[f];
    data.push({ key: "webtools", value });
  }
  
  // Cache the fetched settings for next page load
  if (data) {
    setCachedSettings(data);
  }
  
  return data || [];
};

export function useSiteSettings() {
  const queryClient = useQueryClient();
  
  // Use cached settings as initial data to prevent flash of default content
  const cachedSettings = getCachedSettings();
  
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ["site_settings"],
    queryFn: fetchSiteSettings,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Use cached localStorage data as placeholder to prevent UI flash
    placeholderData: cachedSettings ?? undefined,
  });

  // Cross-tab synchronization: listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SETTINGS_SYNC_KEY) {
        queryClient.invalidateQueries({ queryKey: ["site_settings"] });
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [queryClient]);

  // Helper to optimistically update the cache and notify other tabs
  const updateCacheAndSync = (key: string, value: unknown) => {
    queryClient.setQueryData(["site_settings"], (old: { key: string; value: unknown }[] | undefined) => {
      if (!old) return [{ key, value }];
      const exists = old.some(s => s.key === key);
      if (exists) {
        return old.map(s => s.key === key ? { ...s, value } : s);
      }
      return [...old, { key, value }];
    });
    // Notify other tabs
    localStorage.setItem(SETTINGS_SYNC_KEY, Date.now().toString());
  };

  // Parse settings from query data
  const headerSettings: HeaderSettings = settingsData?.find(s => s.key === "header")?.value as unknown as HeaderSettings || defaultHeaderSettings;
  const footerSettings: FooterSettings = settingsData?.find(s => s.key === "footer")?.value as unknown as FooterSettings || defaultFooterSettings;
  const homeSettings: HomeSettings = settingsData?.find(s => s.key === "home")?.value as unknown as HomeSettings || defaultHomeSettings;
  const aboutSettings: AboutSettings = settingsData?.find(s => s.key === "about")?.value as unknown as AboutSettings || defaultAboutSettings;
  const portfolioSettings: PortfolioSettings = settingsData?.find(s => s.key === "portfolio")?.value as unknown as PortfolioSettings || defaultPortfolioSettings;
  const blogSettings: BlogSettings = settingsData?.find(s => s.key === "blog")?.value as unknown as BlogSettings || defaultBlogSettings;
  const webtoolsSettings: WebtoolsSettings = settingsData?.find(s => s.key === "webtools")?.value as unknown as WebtoolsSettings || defaultWebtoolsSettings;
  const themeSettings: ThemeSettings = settingsData?.find(s => s.key === "theme")?.value as unknown as ThemeSettings || defaultThemeSettings;
  const analyticsSettings: AnalyticsSettings = settingsData?.find(s => s.key === "analytics")?.value as unknown as AnalyticsSettings || defaultAnalyticsSettings;
  const searchSettings: SearchSettings = settingsData?.find(s => s.key === "search")?.value as unknown as SearchSettings || defaultSearchSettings;

  const updateHeaderSettings = async (settings: HeaderSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "header", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("header", serialized);
    }
    return { error };
  };

  const updateFooterSettings = async (settings: FooterSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "footer", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("footer", serialized);
    }
    return { error };
  };

  const updateHomeSettings = async (settings: HomeSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "home", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("home", serialized);
    }
    return { error };
  };

  const updateAboutSettings = async (settings: AboutSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "about", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("about", serialized);
    }
    return { error };
  };

  const updatePortfolioSettings = async (settings: PortfolioSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "portfolio", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("portfolio", serialized);
    }
    return { error };
  };

  const updateBlogSettings = async (settings: BlogSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "blog", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("blog", serialized);
    }
    return { error };
  };

  const updateWebtoolsSettings = async (settings: WebtoolsSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    // Settings loaded outside the dashboard leave out the deprecated tools list.
    // Never let a save from that trimmed copy erase it: merge the stored list back.
    if (serialized.toolsOmitted) {
      delete serialized.toolsOmitted;
      const { data: current } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "webtools")
        .maybeSingle();
      const storedTools = (current?.value as { tools?: unknown } | null)?.tools;
      if (storedTools !== undefined && (!serialized.tools || serialized.tools.length === 0)) {
        serialized.tools = storedTools;
      }
    }
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "webtools", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("webtools", serialized);
    }
    return { error };
  };

  const updateThemeSettings = async (settings: ThemeSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "theme", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("theme", serialized);
    }
    return { error };
  };

  const updateAnalyticsSettings = async (settings: AnalyticsSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "analytics", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("analytics", serialized);
    }
    return { error };
  };

  const updateSearchSettings = async (settings: SearchSettings) => {
    const serialized = JSON.parse(JSON.stringify(settings));
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "search", value: serialized }, { onConflict: "key" });

    if (!error) {
      updateCacheAndSync("search", serialized);
    }
    return { error };
  };

  return {
    headerSettings,
    footerSettings,
    homeSettings,
    aboutSettings,
    portfolioSettings,
    blogSettings,
    webtoolsSettings,
    themeSettings,
    analyticsSettings,
    searchSettings,
    isLoading,
    updateHeaderSettings,
    updateFooterSettings,
    updateHomeSettings,
    updateAboutSettings,
    updatePortfolioSettings,
    updateBlogSettings,
    updateWebtoolsSettings,
    updateThemeSettings,
    updateAnalyticsSettings,
    updateSearchSettings,
    defaultHeaderSettings,
    defaultFooterSettings,
    defaultHomeSettings,
    defaultAboutSettings,
    defaultPortfolioSettings,
    defaultBlogSettings,
    defaultWebtoolsSettings,
    defaultThemeSettings,
    defaultAnalyticsSettings,
    defaultSearchSettings,
  };
}